import Vue from 'vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import EventBus from '@/eventBus'
import SlideApi from '@/api/slide'
import Util from '@/util'
import eventCategories from '@/constant/eventCategories'
import PresentationApi from '@/api/presentation'
import SlideOptionApi from '@/api/slideOption'

const MAX_RAW_ENTRIES = 200

export default (SpinnerWheelTable) => Vue.component('SpinnerWheelTableContainer', {
  data() {
    return {
      entries: [],
      newEntry: '',
      winner: null,
      oldEntries: [],
      oldWinner: null,
      showWinner: false,
      wheelStatus: null,
      wheelEntries: [],
      currentStyle: '',
      currentText: '',
      pendingEntriesCount: 0,
      oldEntryFromAudienceCount: 0,
      countWheelEntries: 0,
      oldSlideActiveId: null,
    }
  },
  computed: {
    ...mapState({
      audiences: (state) => state.audiences,
      slide: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      mapAudienceIdWinnerRemoved: (state) => state.mapAudienceIdWinnerRemoved,
    }),
    ...mapGetters('presentationSetting', ['isShownEmojiAvatar']),
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.slideActive': function (newValue) {
      if (this._isSpinning()) return
      // tạo mapAudienceIdWinnerRemoved = [] nếu chưa tồn tại với mỗi slide để lưu data các winnerRemoved
      if (!this.mapAudienceIdWinnerRemoved[newValue.id]) {
        this.mapAudienceIdWinnerRemoved[newValue.id] = []
      }

      this.handleWinnerRemoved(newValue.id)

      this.getEntries()

      this.oldSlideActiveId = newValue.id
    },
    // eslint-disable-next-line func-names
    '$store.state.presentation.filteringProfanity': function () {
      if (this._isSpinning()) return
      this.getEntries()
    },
  },
  created() {
    this.updateSlideActiveAction({
      loadedSpinnerWheel: false,
      wheelStatus: null,
    })
    this._initWheel()
    EventBus.$on('updateEntries', () => {
      if (this._isSpinning()) return
      this.getEntries()
    })
    EventBus.$on('clearSpinnerWheel', (slideId) => {
      this.clearWheel(slideId)
    })
    EventBus.$on('createEntries', (entries) => {
      this.createNewEntry(entries)
    })
    EventBus.$on('refreshSpinnerWheelEntries', () => {
      this.getSlideOptions()
    })
    this.getSlideOptions()
  },

  destroyed() {
    EventBus.$off('updateEntries')
    EventBus.$off('clearSpinnerWheel')
    EventBus.$off('createEntries')
    EventBus.$off('refreshSpinnerWheelEntries')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    handleWinnerRemoved(slideActiveId) {
      if (this.oldSlideActiveId !== slideActiveId) {
        this.setAudienceRemoved()
      }
    },
    setAudienceRemoved() {
      // Phải reset (show) toàn bộ audiences khi chuyển slide
      Object.keys(this.audiences).forEach((id) => {
        this.audiences[id].removed = false
      })

      const arrAudiences = this.mapAudienceIdWinnerRemoved[this.slide.id]
      if (!arrAudiences) {
        return
      }
      // Với những audiences thuộc winner đã remove thì sẽ remove đi
      arrAudiences.forEach((id) => {
        this.audiences[id].removed = true
      })
      // Remove nốt những winner removed bên Wheel
      this.oldEntries.forEach((item, index) => {
        if (this.audiences[item.id] && this.audiences[item.id].removed) {
          this.oldEntries.splice(index, 1)
        }
      })
    },
    clearWheel(slideId) {
      // slideActive's metadata already cleared when emit event clearSpinnerWheel
      if (slideId !== this.slide.id) return
      this.oldWinner = null
      this.oldEntries = []
      this.getEntries()
      this.selectCurrentEntry(null, null)
    },
    async _initWheel() {
      await this.getOldEntries()
      this.getEntries()
      this.getAudienceOnline()
    },
    getAudienceOnline() {
      PresentationApi.getAudienceOnline({
        accessCode: this.presentation.uniqueAccessCode,
      })
    },
    // for entries list
    getEntries() {
      if (!this.slide.loadedSpinnerWheel) {
        this.updateSlideActiveAction({
          loadedSpinnerWheel: true,
        })
      }
      let entriesFromAudience = []
      let audiencesOnline = []
      if (!this.slide.SlideOptions) {
        this.updateSlideActiveAction({
          SlideOptions: [],
        })
      }
      const entriesFromPresenter = this.slide.SlideOptions
        .filter((option) => option.id)
        .map((option, index) => {
          const defaultEntryTitle = this.$i18nPresenter.t('entry_number', {
            number: index + 1,
          })
          const optionTitle = option && option.title && option.title.trim()

          const name = optionTitle || defaultEntryTitle

          return {
            name,
            id: option.id,
            entryType: 'manual_presenter', // manual_presenter, manual_audience, audience_data, team_data, openended_data
          }
        })

      if (this.slide.metadata && this.slide.metadata.autoFillParticipantName) {
        audiencesOnline = Object.values(this.audiences).filter((audience) => {
          if (audience.removed) {
            return false
          }
          return audience.audienceName
        })

        entriesFromAudience = audiencesOnline.map((audience) => {
          return {
            name: this.isShownEmojiAvatar ? `${audience.audienceEmoji} ${audience.audienceName}` : audience.audienceName,
            id: audience.audience,
            entryType: 'audience_data',
          }
        })
      }
      this._getCurrentEntries(entriesFromPresenter.concat(entriesFromAudience))
      this.entries = entriesFromPresenter
        .filter((e) => e.id)
        .concat(entriesFromAudience.splice(0, MAX_RAW_ENTRIES))
        .map((e) => ({
          ...e,
          name: e.entryType === 'manual_presenter' ? e.name : Util.filterProfaneWordsWrapper(e.name),
        }))
      this.entries.push()
      this._getPendingEntriesCount()
    },
    // for entries for wheel
    _getCurrentEntries(entries) {
      this.wheelEntries = entries.filter((e) => e.id)
      this.wheelEntries.push()
      this.countWheelEntries = this.wheelEntries.length
    },
    _getPendingEntriesCount() {
      if (this.oldWinner === null) {
        this.pendingEntriesCount = 0
        this.oldEntryFromAudienceCount = 0
        return
      }
      const newEntries = new Set(this.entries.map((o) => `${o.entryType}-${o.id}`))
      const olEntries = new Set(this.wheelEntries.map((o) => `${o.entryType}-${o.id}`))
      const pendingEntries = new Set([...newEntries].filter((o) => !olEntries.has(o)))
      const leftEntries = new Set([...olEntries].filter((o) => !newEntries.has(o)))
      this.pendingEntriesCount = pendingEntries.size
      this.oldEntryFromAudienceCount = leftEntries.size
    },
    createNewEntry(entries) {
      if (this._isSpinning()) return
      this.getEntries()

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Spinner Wheel > Entries list > Presenter create new entry',
        null,
        null,
        {
          presentationId: this.presentation.id,
          slideId: this.slide.id,
          quantity: entries.length,
        },
      )
    },

    removeEntry(entry) {
      if (this.oldWinner !== null) {
        const index = this.oldEntries.findIndex((option) => {
          return option.id === entry.id
        })

        if (index !== -1) {
          this.oldEntries.splice(index, 1)
        }
      }

      if (entry.entryType === 'audience_data') {
        this.audiences[entry.id].removed = true
        this.mapAudienceIdWinnerRemoved[this.slide.id].push(entry.id)
      }
      this.getEntries()

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Spinner Wheel > Entries list > Presenter remove entry',
      )
    },

    closeShowWinner() {
      this.showWinner = false
    },

    async stopSpinnerWheel(segment) {
      this.updateSlideActiveAction({
        wheelStatus: null,
      })
      const slideId = this.slide.id
      this.oldWinner = this.winner
      this.winner = null
      setTimeout(() => {
        this.showWinner = true
      }, 1e3)

      await SlideApi.stopTheWheel(slideId, {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        color: segment.fillStyle,
      })
    },

    _isSpinning() {
      if (this.$store.state.slideActive.wheelStatus === 'spinning') return true
      return false
    },

    async startSpinnerWheel() {
      if (this._isSpinning()) return
      this.updateSlideActiveAction({
        wheelStatus: 'spinning',
      })
      const slideId = this.slide.id
      try {
        const { body: response } = await SlideApi.spinTheWheel(slideId, {
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          entries: this.wheelEntries,
        })
        this.winner = response.winner
        const newMetadata = response.metadata || {}
        this.updateSlideActiveAction({
          metadata: newMetadata,
        })
        this.oldEntries = [...this.wheelEntries]
        this._getPendingEntriesCount()
      } catch (e) {
        Sentry.captureException(e)
        this.$store.dispatch('setSyncPresentation', 3)
      }
    },

    async getOldEntries() {
      const slideOptions = this.slide.SlideOptions.map((o) => {
        return o.id
      })
      if (this.slide.metadata) {
        const oldWinner = this.slide.metadata.winner
        if (oldWinner != null) {
          let oldEntries = []
          if (this.slide.metadata.entries.length < MAX_RAW_ENTRIES) {
            oldEntries = this.slide.metadata.entries
          } else if (this.slide.metadata.entryUrl != null) {
            try {
              const res = await this.$http.get(this.slide.metadata.entryUrl)
              oldEntries = res.body
            } catch (e) {
              Sentry.captureException(e)
            }
          }
          oldEntries = oldEntries.filter((o) => {
            return o.entryType === 'audience_data' || (o.entryType === 'presenter_data' && slideOptions.includes(o.id))
          })
          this.oldWinner = oldWinner
          this.oldEntries = oldEntries
        }
      }
    },

    resetSpinnerWheel() {
      if (this._isSpinning()) return
      this.updateSlideActiveAction({
        metadata: {
          ...this.slide.metadata,
          winner: null,
        },
      })
      this.oldWinner = null
      this.oldEntries = []
      this.getEntries()
      this.selectCurrentEntry(null, null)

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Spinner Wheel > Reset spinner wheel',
      )
    },

    selectCurrentEntry(style, text) {
      this.currentStyle = style
      this.currentText = text
    },

    async getSlideOptions() {
      const slideOptions = await SlideOptionApi.getSlideOptionsBySlideId(this.slide.id)
      this.updateSlideActiveAction({
        SlideOptions: slideOptions,
      })
    },
  },

  render(createElement) {
    return createElement(SpinnerWheelTable, {
      props: {
        ...this.$attrs,
        entries: this.entries,
        oldEntries: this.oldEntries,
        newEntry: this.newEntry,
        createNewEntry: this.createNewEntry,
        removeEntry: this.removeEntry,
        startTheWheel: this.startSpinnerWheel,
        stopTheWheel: this.stopSpinnerWheel,
        isUserOwnPresentation: Util.isUserOwnPresentation(this.currentUser, this.presentation),
        winner: this.winner,
        oldWinner: this.oldWinner,
        closeShowWinner: this.closeShowWinner,
        showWinner: this.showWinner,
        wheelEntries: this.wheelEntries,
        resetSpinnerWheel: this.resetSpinnerWheel,
        selectCurrentEntry: this.selectCurrentEntry,
        currentStyle: this.currentStyle,
        currentText: this.currentText,
        pendingEntriesCount: this.pendingEntriesCount,
        oldEntryFromAudienceCount: this.oldEntryFromAudienceCount,
        countWheelEntries: this.countWheelEntries,
      },
    })
  },
})
